import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import type { LeadStatsRefreshDto, LeadStatsSettingsDto, VenueLeadStatsSettingsDto } from '@lib/dto/lead-stats.dto'
import type { CompanyLeadStatsDto, VenueLeadStatsDto } from '@lib/dto/lead-stats-numbers.dto'
import type { LeadStatsQuery, PutLeadStatsSettingsInput, PutVenueLeadStatsAdjustmentsInput } from '@lib/input/lead-stats.input'
import { leadStatsService } from '@/services/LeadStatsService'
import { leadStatsKeys, pipelineKeys } from './queryKeys'

/** The company's lead stats settings. `enabled` is false without the GoHighLevel feature. */
export function useLeadStatsSettings(enabled: boolean): UseQueryResult<LeadStatsSettingsDto> {
  return useQuery({ queryKey: leadStatsKeys.settings(), queryFn: () => leadStatsService.settings(), enabled, retry: false })
}

/** Saves the company's lists whole; every venue's numbers and settings follow. */
export function useSaveLeadStatsSettings(): UseMutationResult<LeadStatsSettingsDto, Error, PutLeadStatsSettingsInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutLeadStatsSettingsInput) => leadStatsService.saveSettings(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(leadStatsKeys.settings(), settings)
      void queryClient.invalidateQueries({ queryKey: leadStatsKeys.venues() })
      // The pipeline's splits are the company's lead stats pipelines.
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.all })
    },
  })
}

/** A venue's adjustments beside the company's definitions. */
export function useVenueLeadStatsSettings(venueId: string, enabled: boolean): UseQueryResult<VenueLeadStatsSettingsDto> {
  return useQuery({
    queryKey: leadStatsKeys.venueSettings(venueId),
    queryFn: () => leadStatsService.venueSettings(venueId),
    enabled: enabled && venueId !== '',
    retry: false,
  })
}

export function useSaveVenueLeadStatsSettings(
  venueId: string,
): UseMutationResult<VenueLeadStatsSettingsDto, Error, PutVenueLeadStatsAdjustmentsInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutVenueLeadStatsAdjustmentsInput) => leadStatsService.saveVenueSettings(venueId, input),
    onSuccess: (settings) => {
      queryClient.setQueryData(leadStatsKeys.venueSettings(venueId), settings)
      void queryClient.invalidateQueries({ queryKey: leadStatsKeys.venue(venueId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.all })
    },
  })
}

/** Reads a venue's GoHighLevel now. Organization admins only. */
export function useRefreshLeadStats(venueId: string): UseMutationResult<LeadStatsRefreshDto, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => leadStatsService.refresh(venueId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: leadStatsKeys.venue(venueId) }),
  })
}

/**
 * A venue's lead stats for a pipeline, view and period. Polls while a read of the
 * venue is under way, so the numbers update when it finishes.
 */
export function useVenueLeadStats(venueId: string, query: LeadStatsQuery, enabled: boolean): UseQueryResult<VenueLeadStatsDto> {
  return useQuery({
    queryKey: leadStatsKeys.numbers(venueId, query.pipeline, query.view, query.period),
    queryFn: () => leadStatsService.venue(venueId, query),
    enabled: enabled && venueId !== '',
    retry: false,
    placeholderData: (previous) => previous,
    refetchInterval: (q) => (q.state.data?.reading === true || q.state.data?.firstReadPending === true ? 5_000 : false),
  })
}

/** Every venue's lead stats for one pipeline, view and period. Organization admins only. */
export function useCompanyLeadStats(query: LeadStatsQuery, enabled: boolean): UseQueryResult<CompanyLeadStatsDto> {
  return useQuery({
    queryKey: leadStatsKeys.summary(query.pipeline, query.view, query.period),
    queryFn: () => leadStatsService.summary(query),
    enabled,
    retry: false,
    placeholderData: (previous) => previous,
  })
}
